<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="breadcrumbs" />
        <h1 class="page-title">{{ table.name || 'Table' }}</h1>
        <p class="page-subtitle">
          <v-chip x-small :color="table.status === 'occupied' ? 'warning' : 'success'" dark class="mr-2">
            {{ table.status === 'occupied' ? 'Occupée' : 'Disponible' }}
          </v-chip>
          <span v-if="order">Commande #{{ order.order_number }}</span>
        </p>
      </div>
      <div class="d-flex" style="gap: 8px;">
        <v-btn
          v-if="canReleaseTable"
          color="error"
          small
          @click="openReleaseDialog"
        >
          <v-icon left small>mdi-lock-open-variant</v-icon>
          Libérer la table
        </v-btn>
        <v-btn text @click="$router.push('/restaurant/tables')">
          <v-icon left small>mdi-arrow-left</v-icon>
          Retour
        </v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable && order">
      <v-row>
        <!-- Left side: Order details -->
        <v-col cols="12" md="7">
          <!-- Summary cards -->
          <v-row class="mb-3">
            <v-col cols="6" sm="3">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Date</div>
                <div class="stat-value">{{ formatDate(order.opened_at || order.created_at) }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Serveur</div>
                <div class="stat-value">{{ order.server_details ? order.server_details.name : '—' }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Couverts</div>
                <div class="stat-value">{{ order.guest_count }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Total</div>
                <div class="stat-value font-weight-bold">{{ fmt(order.total) }}</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Items table -->
          <v-card flat class="table-card">
            <div class="pa-3 d-flex justify-space-between align-center">
              <span class="font-weight-bold" style="font-size: 0.875rem;">Articles</span>
              <v-chip x-small color="primary" dark>{{ (order.order_products || []).length }} article(s)</v-chip>
            </div>
            <v-divider></v-divider>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th class="text-left">PRODUIT</th>
                  <th class="text-center">QTÉ</th>
                  <th class="text-right">PRIX</th>
                  <th class="text-right">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in order.order_products" :key="p.id">
                  <td class="text-left">
                    <span class="font-weight-medium">{{ p.product_name }}</span>
                    <br v-if="p.product_category" />
                    <span v-if="p.product_category" class="text-caption text--secondary">{{ p.product_category }}</span>
                    <br v-if="p.notes" />
                    <span v-if="p.notes" class="text-caption" style="color: #FF9800; font-style: italic;">* {{ p.notes }}</span>
                  </td>
                  <td class="text-center">{{ p.quantity }}</td>
                  <td class="text-right">{{ fmt(p.price) }}</td>
                  <td class="text-right font-weight-medium">{{ fmt(p.total) }}</td>
                </tr>
                <tr v-if="!order.order_products || order.order_products.length === 0">
                  <td colspan="4" class="text-center text--secondary pa-4">Aucun article</td>
                </tr>
              </tbody>
              <tfoot v-if="order.order_products && order.order_products.length > 0">
                <tr class="totals-foot">
                  <td colspan="3" class="text-right">Sous-total</td>
                  <td class="text-right">{{ fmt(order.subtotal) }}</td>
                </tr>
                <tr class="totals-foot" v-if="order.taxe_percent > 0">
                  <td colspan="3" class="text-right">Taxe ({{ order.taxe_percent }}%)</td>
                  <td class="text-right">{{ fmt(order.taxe_amount) }}</td>
                </tr>
                <tr class="totals-foot" v-if="order.service_fee_percent > 0">
                  <td colspan="3" class="text-right">Service ({{ order.service_fee_percent }}%)</td>
                  <td class="text-right">{{ fmt(order.service_fee_amount) }}</td>
                </tr>
                <tr class="totals-foot">
                  <td colspan="3" class="font-weight-bold text-right">TOTAL</td>
                  <td class="text-right font-weight-bold">{{ fmt(order.total) }}</td>
                </tr>
              </tfoot>
            </v-simple-table>
          </v-card>
        </v-col>

        <!-- Right side: Activity log -->
        <v-col cols="12" md="5">
          <v-card flat class="table-card">
            <div class="pa-3">
              <span class="font-weight-bold" style="font-size: 0.875rem;">
                <v-icon small class="mr-1">mdi-history</v-icon>
                Historique
              </span>
            </div>
            <v-divider></v-divider>
            <div class="log-container">
              <div v-for="log in (order.logs || [])" :key="log.id" class="log-entry">
                <div class="log-dot" :class="logColor(log.action)"></div>
                <div class="log-content">
                  <div class="log-action">
                    <v-icon x-small class="mr-1">{{ logIcon(log.action) }}</v-icon>
                    {{ log.description }}
                  </div>
                  <div class="log-meta">
                    <span v-if="log.performed_by">{{ log.performed_by }}</span>
                    <span v-if="log.supervisor_code"> — Code: {{ log.supervisor_code }}</span>
                    <span class="ml-2">{{ formatTime(log.created_at) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="!order.logs || order.logs.length === 0" class="pa-4 text-center text--secondary">
                Aucune activité
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <div v-if="enable && !order" class="text-center pa-6">
      <v-icon size="48" color="grey lighten-1">mdi-table-furniture</v-icon>
      <p class="mt-3 text--secondary">Cette table n'a pas de commande active.</p>
    </div>

    <!-- Release table dialog -->
    <v-dialog v-model="releaseDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="error">mdi-lock-open-variant</v-icon>
          Libérer la table
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            La commande en cours sera annulée et la table redeviendra disponible.
            Cette action est irréversible. Veuillez indiquer la raison.
          </p>
          <v-textarea
            v-model="releaseReason"
            label="Commentaire / Raison"
            outlined
            dense
            rows="3"
            auto-grow
            :error-messages="releaseError"
            @input="releaseError = ''"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="releasing" @click="releaseDialog = false">Annuler</v-btn>
          <v-btn color="error" :loading="releasing" @click="confirmRelease">
            Confirmer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import PreloaderComponent from '../components/PreloaderComponent.vue'
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import moment from 'moment'
import numeral from 'numeral'

export default {
  name: 'TableDetailView',
  components: { PreloaderComponent, BreadcrumbComponent },
  data: () => ({
    enable: false,
    table: {},
    order: null,
    breadcrumbs: [
      { text: 'Restaurant', disabled: true },
      { text: 'Tables', disabled: false, href: '/restaurant/tables' },
      { text: 'Détails', disabled: true },
    ],
    releaseDialog: false,
    releaseReason: '',
    releaseError: '',
    releasing: false,
    snackbar: { show: false, text: '', color: 'success', timeout: 3000 },
  }),
  computed: {
    canReleaseTable() {
      if (!this.order) return false
      var s = (this.order.status || '').toLowerCase()
      return s === 'open'
    },
  },
  methods: {
    async load() {
      try {
        var tableId = this.$route.params.id

        // Load table info
        var tableRes = await this.$axios.get('/restaurant-tables/' + tableId)
        this.table = tableRes.data

        // If table has active order, load it
        if (this.table.current_order_id) {
          var orderRes = await this.$axios.get('/orders/' + this.table.current_order_id)
          this.order = orderRes.data
        }

        this.enable = true
      } catch (e) {
        console.error('Error loading table details:', e)
        this.enable = true
      }
    },
    fmt(v) {
      return numeral(v || 0).format('0,0.00') + ' HTG'
    },
    formatDate(d) {
      if (!d) return '—'
      return moment(d).format('DD/MM/YYYY HH:mm')
    },
    formatTime(d) {
      if (!d) return ''
      return moment(d).format('HH:mm')
    },
    logIcon(action) {
      switch (action) {
        case 'table_opened': return 'mdi-door-open'
        case 'table_closed': return 'mdi-door-closed'
        case 'item_added': return 'mdi-plus-circle'
        case 'item_removed': return 'mdi-minus-circle'
        case 'item_qty_changed': return 'mdi-pencil'
        case 'order_cancelled': return 'mdi-cancel'
        default: return 'mdi-information'
      }
    },
    logColor(action) {
      switch (action) {
        case 'table_opened': return 'dot-green'
        case 'table_closed': return 'dot-red'
        case 'item_added': return 'dot-blue'
        case 'item_removed': return 'dot-orange'
        case 'item_qty_changed': return 'dot-yellow'
        case 'order_cancelled': return 'dot-red'
        default: return 'dot-grey'
      }
    },
    openReleaseDialog() {
      this.releaseReason = ''
      this.releaseError = ''
      this.releaseDialog = true
    },
    currentUser() {
      try {
        return JSON.parse(localStorage.authentication || '{}').user || null
      } catch { return null }
    },
    async confirmRelease() {
      var reason = (this.releaseReason || '').trim()
      if (!reason) {
        this.releaseError = 'Veuillez indiquer une raison.'
        return
      }
      if (!this.order || !this.order.id) {
        this.releaseError = 'Aucune commande active à annuler.'
        return
      }
      this.releasing = true
      try {
        var user = this.currentUser()
        var payload = {
          reason: reason,
          performed_by: user ? user.full_name : null,
          performed_by_id: user ? user.id : null,
          closed_by: user ? user.id : null,
        }
        await this.$axios.post('/orders/' + this.order.id + '/cancel', payload)
        this.releaseDialog = false
        this.snackbar = { show: true, text: 'Table libérée', color: 'success', timeout: 3000 }
        await this.load()
      } catch (e) {
        var msg = (e && e.response && e.response.data && e.response.data.message) || 'Erreur lors de la libération'
        this.snackbar = { show: true, text: msg, color: 'error', timeout: 4000 }
      } finally {
        this.releasing = false
      }
    },
  },
  mounted() {
    this.load()
    // Auto-refresh every 15 seconds
    this._refreshInterval = setInterval(() => { this.load() }, 15000)
  },
  beforeDestroy() {
    if (this._refreshInterval) clearInterval(this._refreshInterval)
  },
}
</script>

<style scoped>
.stat-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
}
.stat-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}
.stat-value {
  font-size: 0.9375rem;
  color: #1e293b;
  margin-top: 2px;
}
.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  overflow: hidden;
}
.table-card .v-simple-table thead th {
  font-size: 0.6875rem !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8 !important;
  border-bottom: 1px solid #e8ecf1 !important;
  padding: 8px 16px !important;
}
.table-card .v-simple-table tbody td {
  font-size: 0.8125rem;
  padding: 10px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
.totals-foot td {
  font-size: 0.875rem !important;
  padding: 12px 16px !important;
  border-top: 2px solid #e8ecf1 !important;
  background: #f8fafc;
}
.log-container {
  max-height: 500px;
  overflow-y: auto;
}
.log-entry {
  display: flex;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  align-items: flex-start;
  gap: 10px;
}
.log-entry:last-child {
  border-bottom: none;
}
.log-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}
.dot-green { background: #4CAF50; }
.dot-red { background: #F44336; }
.dot-blue { background: #2196F3; }
.dot-orange { background: #FF9800; }
.dot-yellow { background: #FFC107; }
.dot-grey { background: #9E9E9E; }
.log-content {
  flex: 1;
}
.log-action {
  font-size: 0.8125rem;
  color: #1e293b;
}
.log-meta {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-top: 2px;
}
</style>
